import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, CheckCircle2, Newspaper, Video, FileText, FolderSearch, ClipboardList, CalendarDays, Brain, Cloud, GitBranch, ShieldCheck, Code2, Workflow, Boxes } from "lucide-react";
import { CERTIFICATIONS, STATS, TEAM_IMG, SPRINGPEOPLE_TRACKS } from "../mock";

const TRACK_ICONS = { Brain, Cloud, GitBranch, ShieldCheck, Code2, Workflow, ClipboardList, Boxes };
const RES_ICONS = { Newspaper, Video, FileText, FolderSearch, ClipboardList, CalendarDays };
const RES_LINKS = {
  "Blog": "/resources/blog", "Webinars": "/resources/webinars", "Whitepapers": "/resources/whitepapers",
  "Case Studies": "/resources/case-studies", "Practice Tests": "/resources/practice-tests", "Events": "/resources/events"
};

export const CertificationsPage = () => (
  <div className="bg-white min-h-screen">
    <Header/>
    <PageHero eyebrow="CERTIFICATIONS" title="Industry Certifications" subtitle="Globally recognized certifications with 95% pass rate."/>

    {/* SpringPeople-inspired course tracks organised by category */}
    <section className="max-w-7xl mx-auto px-4 py-16">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">COURSES & CERTIFICATIONS</div>
          <h2 className="text-[#15348C]">Train across 8 enterprise tracks</h2>
          <p className="mt-3 text-gray-600">Officially aligned to AWS, Microsoft, Google Cloud, NVIDIA, Red Hat, ISC2 and 35+ vendors.</p>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-6">
        {SPRINGPEOPLE_TRACKS.map((t, i) => {
          const Icon = TRACK_ICONS[t.icon] || BookOpen;
          return (
            <Reveal key={t.category} variant="up" delay={(i % 4) * 70}>
              <div className="sg-card group bg-white border border-gray-100 rounded-2xl p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#15348C] flex items-center justify-center group-hover:bg-[#6BBE32] transition-colors">
                    <Icon size={22} className="text-white"/>
                  </div>
                  <h3 className="text-[#15348C]" style={{fontSize:"1.2rem"}}>{t.category}</h3>
                  <span className="ml-auto text-xs bg-[#6BBE32]/15 text-[#4F9622] px-2 py-1 rounded-full font-semibold">{t.courses.length} courses</span>
                </div>
                <ul className="space-y-2 text-sm">
                  {t.courses.map(c => (
                    <li key={c} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle2 size={14} className="text-[#6BBE32] mt-1 shrink-0"/> {c}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="mt-5 inline-flex items-center gap-1 text-sm text-[#F57C20] font-semibold sg-link">View track →</Link>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>

    {/* Certifications by vendor */}
    <section className="bg-[#F8F9FA] py-16">
      <div className="max-w-7xl mx-auto px-4">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">TOP CERTIFICATIONS</div>
            <h2 className="text-[#15348C]">Globally recognized certifications</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CERTIFICATIONS.map((c, i) => (
            <Reveal key={c.name} variant="up" delay={(i % 6) * 60}>
              <div className="sg-card bg-white border border-gray-100 rounded-xl p-6 group h-full">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs bg-[#15348C] text-white px-2 py-1 rounded font-semibold">{c.vendor}</span>
                  <span className="text-xs bg-[#6BBE32]/15 text-[#4F9622] px-2 py-1 rounded font-semibold">{c.level}</span>
                </div>
                <h3 className="text-[#15348C] mb-4 leading-snug group-hover:text-[#6BBE32] transition-colors" style={{fontSize:"1.05rem"}}>{c.name}</h3>
                <div className="space-y-1.5 mb-4">
                  <div className="flex justify-between text-[11px] text-gray-600"><span>Pass Rate</span><span className="font-bold text-[#4F9622]">{c.passRate}%</span></div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-[#6BBE32] transition-all duration-700" style={{ width: `${c.passRate}%` }}/></div>
                </div>
                <div className="pt-4 border-t border-gray-100 flex justify-between text-sm">
                  <span className="text-gray-500">Exam: {c.duration}</span>
                  <Link to="/contact" className="sg-link text-[#F57C20] font-semibold">Enroll →</Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
    <Footer/>
  </div>
);

export const ResourcesPage = () => {
  const { RESOURCES } = require("../mock");
  return (
    <div className="bg-white min-h-screen">
      <Header/>
      <PageHero eyebrow="RESOURCES" title="Resources" subtitle="Knowledge hub for learners, leaders and enterprises."/>
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {RESOURCES.map((r, i) => {
            const Icon = RES_ICONS[r.icon] || BookOpen;
            return (
              <Reveal key={r.title} variant="up" delay={(i % 6) * 60}>
                <Link to={RES_LINKS[r.title] || "/resources"} className="sg-card block bg-[#F8F9FA] border border-gray-100 rounded-xl p-6">
                  <div className="w-12 h-12 rounded-lg bg-[#6BBE32] flex items-center justify-center mb-4">
                    <Icon size={22} className="text-white"/>
                  </div>
                  <h3 className="text-[#15348C]" style={{fontSize:"1.1rem"}}>{r.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{r.desc}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm text-[#F57C20] font-semibold sg-link">Explore <ArrowRight size={13}/></div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>
      <Footer/>
    </div>
  );
};

export const AboutPage = () => (
  <div className="bg-white min-h-screen">
    <Header/>
    <PageHero eyebrow="ABOUT US" title="About SkillsGain" subtitle="Empowering the future of work through world-class learning."/>
    <section className="max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-12 items-center">
      <Reveal variant="left">
        <img src={TEAM_IMG} alt="team" className="rounded-2xl shadow-xl w-full h-[420px] object-cover"/>
      </Reveal>
      <Reveal variant="right" delay={80}>
        <h2 className="text-[#15348C]">Building the workforce of tomorrow, today.</h2>
        <p className="mt-4 text-gray-600 leading-relaxed">Founded with a mission to democratize world-class technology education, SkillsGain has trained 500,000+ professionals across 60+ countries. We partner with the world’s leading technology vendors to deliver authorized, vendor-approved curricula at scale.</p>
        <p className="mt-3 text-gray-600 leading-relaxed">From Generative AI to Cloud, from Cybersecurity to DevOps, we are obsessed with creating learning experiences that translate directly to business outcomes.</p>
        <ul className="mt-5 space-y-2 text-sm text-[#15348C]">
          {["Vendor-authorized curriculum", "Live virtual & on-site delivery", "Dedicated enterprise success managers", "95% certification pass rate"].map(t => (
            <li key={t} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#6BBE32]"/>{t}</li>
          ))}
        </ul>
        <div className="mt-8 grid grid-cols-2 gap-4">
          {STATS.map(s => (
            <div key={s.label} className="sg-card bg-[#F8F9FA] rounded-xl p-4 border border-gray-100">
              <div className="text-2xl font-bold text-[#6BBE32]" style={{fontFamily:"Times New Roman, serif"}}>{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
    <Footer/>
  </div>
);
